/*
 * HVACloud AI Assistant — global toolbar entry.
 *
 * Sits in the AppShell topbar. Robot glyph + violet accent, so it reads as
 * "the assistant" independent of the current page. On click it opens a
 * dropdown menu of context-appropriate actions (schema, units, dashboard
 * ideas, …) that changes based on the current route.
 *
 * Rendering the actual task modals is delegated to task-specific components
 * (AiSchemaModal / AiUnitsModal / AiDashboardModal) so this component stays
 * a thin dispatcher.
 */

import type * as preact from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { AiSchemaModal } from './AiSchemaModal';
import { AiUnitsModal } from './AiUnitsModal';
import { AiDashboardModal } from './AiDashboardModal';
import { AiChartPresetLauncher } from './AiChartPresetLauncher';
import { AiDocumentationParserModal } from './AiDocumentationParserModal';
import { HeroAiSchema, HeroRegisterExtract } from '../../icons/HvacIcons';
import { api } from '../../api/client';
import type { ListPlantsResponse } from '../../api/types';
import { useAsync } from '../../hooks/useAsync';
import { showToast } from '../Toaster';
import './ai.css';
import { BRAND } from '../../brand/brand';

type ActionKind = 'schema' | 'units' | 'dashboard' | 'chart-preset' | 'documentation-parser';

interface Action {
  kind: ActionKind;
  title: string;
  description: string;
  disabled?: string; // reason, if any
}

/** Extract a plant id from the current route. Both /plants/:id/... and
 *  /dashboard/:id carry one; /dashboard on its own (plant list) does not. */
function currentPlantId(path: string): string | null {
  const m = path.match(/^\/(?:plants|dashboard)\/([0-9a-fA-F-]{36})/);
  return m ? m[1] : null;
}

/** True iff the current route is the dashboard view for a specific plant.
 *  /dashboard on its own is the plant-picker list; the assistant has
 *  nothing to do there, so it stays hidden. */
function isDashboardRoute(path: string): boolean {
  return /^\/dashboard\/[0-9a-fA-F-]{36}/.test(path);
}

/** True iff the current route is the DP-Charts page for a specific plant. */
function isChartsRoute(path: string): boolean {
  return /^\/plants\/[0-9a-fA-F-]{36}\/charts/.test(path);
}

/** Routes where the Documentation-Parser is the natural next step: the scan
 *  candidates page (operator is looking at devices and needs a Modbus map)
 *  and the schema-templates catalog (the operator manages the outputs). */
function isDocumentationParserRoute(path: string): boolean {
  return /^\/plants\/[0-9a-fA-F-]{36}\/discovery\/candidates/.test(path)
      || /^\/settings\/typicals\b/.test(path);
}

export function AiAssistantButton() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openTask, setOpenTask] = useState<{ kind: ActionKind; plantId: string } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Preload the plant catalog so the dashboard action can pick a target
  // plant. Cheap fetch, cached by useAsync semantics.
  const plantsState = useAsync(
    (signal) => api.get<ListPlantsResponse>('/api/plants', signal),
    [],
  );

  const path = location.path;
  const plantIdFromRoute = currentPlantId(path);
  const onCharts = isChartsRoute(path);
  const onPlantEditor = /^\/plants\/[0-9a-fA-F-]{36}/.test(path) && !onCharts;
  const onDashboardWithPlant = isDashboardRoute(path);
  const onDocumentationParserRoute = isDocumentationParserRoute(path);

  const actions: Action[] = useMemo(() => {
    const list: Action[] = [];
    // Schema + Einheiten sind DP-Editor-Aktionen (Baum-Struktur), also nur
    // dort. Auf der Dashboard-Seite eines Plants gibt es sie bewusst nicht —
    // Techniker soll fürs Umsortieren des Baumes in die Anlagenverwaltung
    // wechseln.
    if (onPlantEditor && plantIdFromRoute) {
      list.push({
        kind: 'schema',
        title: 'Schema-Gruppierung vorschlagen',
        description: `${BRAND.productName} analysiert die Datenpunkte und schlägt eine sinnvolle Gruppierung in Untersysteme vor.`,
      });
      list.push({
        kind: 'units',
        title: 'Einheiten ergänzen',
        description: `${BRAND.productName} schlägt für Datenpunkte ohne Einheit einen passenden Wert vor (°C, bar, kW …).`,
      });
    }
    // Dashboard-Vorschlag: entweder wir sind auf dem Dashboard einer
    // konkreten Anlage, oder wir sind im DP-Editor (dann brauchen wir auch
    // eine plantId — ist als plantIdFromRoute vorhanden).
    if (onDashboardWithPlant || onPlantEditor) {
      list.push({
        kind: 'dashboard',
        title: 'Dashboard-Seiten vorschlagen',
        description: `${BRAND.productName} entwirft ein oder mehrere Dashboard-Seiten basierend auf den vorhandenen Datenpunkten.`,
      });
    }
    // Chart-Preset-Vorschlag: nur auf der DP-Charts-Seite. Der Modal geht
    // im Erfolgsfall direkt in die aktuelle Auswahl der PlantChartsPage —
    // das Zusammenspiel lebt drueben; hier feuern wir nur ein Custom-Event
    // damit die Seite den Modal oeffnet ohne dass wir die Modal-Instanz
    // hier duplizieren.
    if (onCharts && plantIdFromRoute) {
      list.push({
        kind: 'chart-preset',
        title: 'Auswertung mit KI zusammenstellen',
        description: `${BRAND.productName} schlägt anhand deiner Beschreibung passende Datenpunkte, Zeitfenster und Darstellung für ein Chart-Preset vor.`,
      });
    }
    // Doku-Parser: keine plantId nötig (Ergebnis wird als globales
    // Schema-Template gespeichert und kann auf jede Anlage angewendet
    // werden). Sichtbar auf der Scan-Kandidaten-Seite (Operator ist
    // gerade am Klassifizieren, braucht eine Register-Map für ein
    // unbekanntes Gerät) und im Template-Katalog.
    if (onDocumentationParserRoute) {
      list.push({
        kind: 'documentation-parser',
        title: 'Register aus Doku ableiten',
        description: `PDF-Handbuch oder CSV hochladen — ${BRAND.productName} extrahiert Modbus-Register und legt ein Schema-Template an.`,
      });
    }
    return list;
  }, [plantIdFromRoute, onPlantEditor, onDashboardWithPlant, onCharts, onDocumentationParserRoute]);

  // Hide the button entirely on routes that have nothing for the assistant
  // to do. Keeps the topbar tidy — no button that opens an empty menu.
  if (actions.length === 0) return null;

  // Close menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const runAction = (kind: ActionKind) => {
    setMenuOpen(false);
    // Doku-Parser is plant-independent — the resulting template lives in
    // the tenant-wide catalog. Pass an empty plantId sentinel so the
    // renderer knows to skip plant-scoped fetches.
    if (kind === 'documentation-parser') {
      setOpenTask({ kind, plantId: '' });
      return;
    }
    // For units/schema we need a plant id — route-scoped. For dashboard we
    // fall back to the plant already in route, else user has to pick (deferred).
    const plantId = plantIdFromRoute
      ?? (plantsState.status === 'ok' ? plantsState.data.plants[0]?.id : null);
    if (!plantId) return;
    setOpenTask({ kind, plantId });
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        <button
          ref={buttonRef}
          type="button"
          class="hv-ai-btn"
          title={`${BRAND.aiName}-Assistent`}
          aria-label={`${BRAND.aiName}-Assistent`}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <RobotIcon />
        </button>

        {menuOpen && (
          <>
            <div class="hv-ai-menu-backdrop" onClick={() => setMenuOpen(false)} />
            <div class="hv-ai-menu" role="menu" onClick={(e) => e.stopPropagation()}>
              <div class="hv-ai-menu__header">Aktionen für diese Seite</div>
              {actions.map((a) => (
                <button
                  key={a.kind}
                  type="button"
                  role="menuitem"
                  class="hv-ai-menu__item"
                  disabled={!!a.disabled}
                  title={a.disabled}
                  onClick={() => !a.disabled && runAction(a.kind)}
                >
                  <ActionIcon kind={a.kind} />
                  <span class="hv-ai-menu__item-body">
                    <span class="hv-ai-menu__item-title">{a.title}</span>
                    <span class="hv-ai-menu__item-desc">{a.disabled ? a.disabled : a.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {openTask?.kind === 'schema' && (
        <AiSchemaModal plantId={openTask.plantId} onClose={() => setOpenTask(null)} />
      )}
      {openTask?.kind === 'units' && (
        <AiUnitsModal plantId={openTask.plantId} onClose={() => setOpenTask(null)} />
      )}
      {openTask?.kind === 'dashboard' && (
        <AiDashboardModal plantId={openTask.plantId} onClose={() => setOpenTask(null)} />
      )}
      {openTask?.kind === 'chart-preset' && (
        <AiChartPresetLauncher plantId={openTask.plantId} onClose={() => setOpenTask(null)} />
      )}
      {openTask?.kind === 'documentation-parser' && (
        <AiDocumentationParserModal
          onClose={() => setOpenTask(null)}
          onSaved={() => {
            showToast({
              tone: 'success',
              title: 'Template gespeichert',
              message: 'Das neue Schema-Template steht unter „Schema-Vorlagen" bereit.',
              autoHideAfterMs: 5000,
            });
          }}
        />
      )}
    </>
  );
}

function RobotIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"
         stroke-linecap="round" stroke-linejoin="round">
      <rect x="4" y="6" width="12" height="9" rx="2" />
      <circle cx="8"  cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="10" r="1" fill="currentColor" stroke="none" />
      <path d="M10 3v3" />
      <path d="M8 15v2M12 15v2" />
      <path d="M2 10h2M16 10h2" />
    </svg>
  );
}

function ActionIcon({ kind }: { kind: ActionKind }) {
  // Der Menü-Icon-Slot: 40x40 damit die Hero-Animationen (Wolke,
  // Baum-Wachstum, Scanline) auf der zur Verfügung stehenden Fläche
  // erkennbar bleiben. --ai-accent färbt currentColor.
  const size = 40;
  const wrap = (child: preact.ComponentChildren) => (
    <span class="hv-ai-menu__item-icon" style={{ color: 'var(--ai-accent, var(--accent))', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {child}
    </span>
  );
  switch (kind) {
    case 'schema':
      return wrap(<HeroAiSchema size={size} />);
    case 'documentation-parser':
      return wrap(<HeroRegisterExtract size={size} />);
    case 'units':
      return wrap(
        <svg viewBox="0 0 96 96" width={size} height={size} fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          {/* Waagerechte "Skala" mit pulsierenden Ticks — °C / bar / kW */}
          <path d="M12 32h72M12 50h72M12 68h72" opacity=".6" />
          {[20, 38, 56, 74].map((x, i) => (
            <g key={x} class="hv-pop" style={{ animationDelay: `${i * 0.35}s`, transformOrigin: `${x}px 50px` }}>
              <rect x={x - 3} y="47" width="6" height="6" fill="currentColor" stroke="none" rx="1" />
            </g>
          ))}
          <text x="52" y="27" font-size="16" font-family="ui-monospace, monospace" fill="currentColor" stroke="none" opacity=".7">°C</text>
        </svg>
      );
    case 'dashboard':
      return wrap(
        <svg viewBox="0 0 96 96" width={size} height={size} fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          {/* 2x2 Grid, Tiles wachsen gestaffelt */}
          {[
            { x: 12, y: 12, w: 32, h: 36, d: 0 },
            { x: 52, y: 12, w: 32, h: 20, d: 0.35 },
            { x: 12, y: 56, w: 32, h: 28, d: 0.7 },
            { x: 52, y: 40, w: 32, h: 44, d: 1.05 },
          ].map((t, i) => (
            <g key={i} class="hv-grow-slow" style={{ animationDelay: `${t.d}s` }}>
              <rect x={t.x} y={t.y} width={t.w} height={t.h} rx="3" />
            </g>
          ))}
        </svg>
      );
    case 'chart-preset':
      return wrap(
        <svg viewBox="0 0 96 96" width={size} height={size} fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          {/* Achsen + gezeichnete Kurve */}
          <path d="M14 78 H86 M14 14 V78" opacity=".55" />
          <path d="M18 66 L34 44 L48 54 L64 24 L80 32" class="hv-chart" />
          <circle cx="34" cy="44" r="3" fill="currentColor" stroke="none" class="hv-pop" style={{ animationDelay: '.4s', transformOrigin: '34px 44px' }} />
          <circle cx="64" cy="24" r="3" fill="currentColor" stroke="none" class="hv-pop" style={{ animationDelay: '.8s', transformOrigin: '64px 24px' }} />
        </svg>
      );
  }
}
